import { Link } from "react-router-dom";
import { LineReveal, FadeUp } from "../components/Reveal";
import { ArrowUpRight, GraduationCap, BookOpen, FileText, Briefcase, School } from "lucide-react";

const services = [
  {
    code: "S/01",
    icon: GraduationCap,
    tag: "For Class 11–12 Students",
    title: "Students, Class 11 &amp; 12",
    body:
      "Choose courses and in-demand skills that are actually moving the job market — not just chase college brand names. We help you understand which skills from your domains are dominating global services, the roles that are hiring, and how to get into them before you leave school.",
    outcomes: [
      "In-demand skill mapping across your subjects",
      "Course &amp; college shortlist matched to real careers",
      "First-principles view of the global job market",
      "A written roadmap for the next 3 years",
    ],
  },
  {
    code: "S/02",
    icon: BookOpen,
    tag: "For Undergraduates",
    title: "College Students",
    body:
      "College is one of the best parts of life — but too many students drift into careers by copying seniors, friends, or hearsay, without doing their own market research. We build a custom career plan based on your current programme, or help you pivot cleanly if your programme no longer fits.",
    outcomes: [
      "Real market research on your degree &amp; alternatives",
      "Custom career plan (stay-and-optimise or pivot)",
      "Skill stacks that make you employable, not just qualified",
      "Internship &amp; entry-level positioning",
    ],
  },
  {
    code: "S/03",
    icon: FileText,
    tag: "For Aspirants",
    title: "Government Exam Aspirants",
    body:
      "Government preparation is difficult, competition keeps rising, and it&rsquo;s easy to lose your most productive years to a path that&rsquo;s uncertain. We&rsquo;ve been on that road — so we help you decide, honestly: continue your preparation with a clear timeline, or move to a private-sector path with a real plan.",
    outcomes: [
      "Honest, unbiased assessment of your current preparation",
      "Timeline &amp; runway analysis — how long is worth it?",
      "Backup plan design (parallel private-sector track)",
      "Transition roadmap if you decide to switch",
    ],
  },
  {
    code: "S/04",
    icon: Briefcase,
    tag: "For Working Professionals",
    title: "Professionals",
    body:
      "Most professionals aren&rsquo;t pleased with their career choices, and many are stuck in low-paying, easily-replaceable skills. We help you find the best possible next path — a skill that&rsquo;s realistic to transition to, and the specific methods to get you into that career.",
    outcomes: [
      "Skills &amp; strengths audit",
      "Target role identification (non-IT &amp; entry-level IT)",
      "Transition plan: upskilling, positioning, applications",
      "Interview prep with an actual recruiter&rsquo;s lens",
    ],
  },
  {
    code: "S/05",
    icon: School,
    tag: "For Schools &amp; Colleges",
    title: "Institutions",
    body:
      "Schools and colleges are integral to a student&rsquo;s growth, but most miss out on giving the right career advice — they don&rsquo;t have a team who understands how professional job markets actually work, so students get pushed toward traditional paths. We partner with your institution to guide students toward the most relevant careers, and teach them how the job market functions so they can choose well the first time.",
    outcomes: [
      "Onsite career consulting drives",
      "Group psychometric assessments",
      "Faculty &amp; parent orientation sessions",
      "Ongoing advisory partnership",
    ],
  },
];

export default function Services() {
  return (
    <main data-testid="services-page" className="pt-[112px]">
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="flex items-center gap-3 mb-10">
            <span className="rule" /><span className="overline">The Practice</span>
          </div>
          <h1 className="font-serif font-light tracking-[-0.02em] leading-[0.94] text-[12vw] md:text-[8.5vw]">
            <LineReveal
              lines={[
                <>Services, considered.</>,
                <>Never <em className="italic text-[#C26D5C]">packaged</em>.</>,
              ]}
              stagger={0.12}
              delay={0.1}
            />
          </h1>
          <FadeUp delay={0.4}>
            <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed text-[#1A1C1A]/80">
              Five practices, each shaped for where you actually are in your career journey. Sessions are online nationwide, with onsite drives available for schools, colleges, and institutions.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Swiss Grid */}
      <section className="pb-24">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="border-t border-[#1A1C1A]">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <FadeUp key={s.code} delay={i * 0.04}>
                  <div className="grid grid-cols-12 gap-4 md:gap-10 border-b border-[#1A1C1A] py-12 md:py-16 group hover:bg-[#EAE6E1] transition-colors duration-500">
                    <div className="col-span-12 md:col-span-1 overline text-[#C26D5C]">{s.code}</div>
                    <div className="col-span-12 md:col-span-5">
                      <div className="flex items-center gap-3 text-[#1A1C1A]/60 mb-4">
                        <Icon size={18} strokeWidth={1.5} />
                        <span className="overline">{s.tag.replace('&amp;', '&')}</span>
                      </div>
                      <h2
                        className="font-serif text-4xl md:text-5xl font-light tracking-tight leading-[1.02]"
                        dangerouslySetInnerHTML={{ __html: s.title }}
                      />
                    </div>
                    <div className="col-span-12 md:col-span-3">
                      <p
                        className="text-base leading-relaxed text-[#1A1C1A]/85"
                        dangerouslySetInnerHTML={{ __html: s.body }}
                      />
                    </div>
                    <div className="col-span-12 md:col-span-3">
                      <div className="overline text-[#1A1C1A]/60 mb-4">What you walk away with</div>
                      <ul className="space-y-2 text-sm">
                        {s.outcomes.map((inc) => (
                          <li key={inc} className="flex items-start gap-2">
                            <span className="text-[#C26D5C] mt-[10px] w-2 h-px bg-[#C26D5C]" />
                            <span dangerouslySetInnerHTML={{ __html: inc }} />
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/book"
                        data-testid={`service-cta-${s.code.replace('/', '-')}`}
                        className="inline-flex items-center gap-2 mt-8 text-sm border-b border-[#1A1C1A] pb-1 hover:text-[#C26D5C] hover:border-[#C26D5C] transition-colors"
                      >
                        Book a Free Call <ArrowUpRight size={14} />
                      </Link>
                    </div>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-[#EAE6E1] border-y border-[#D1CDC7]">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <FadeUp>
            <div className="flex items-center gap-3 mb-8">
              <span className="rule" /><span className="overline">The Process</span>
            </div>
            <h2 className="font-serif text-5xl md:text-6xl font-light leading-[0.98] tracking-tight max-w-3xl mb-16">
              How an engagement <em className="italic text-[#C26D5C]">actually</em> begins.
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { n: "01", t: "Book Your Session", d: "A free discovery call, or a full assessment-based session — online, or onsite for institutions." },
              { n: "02", t: "Assess & Understand", d: "Complete a psychometric assessment (where applicable) so we understand your strengths, interests, and goals before we talk." },
              { n: "03", t: "One-on-One Counselling", d: "Sit down with an expert who has worked inside the hiring process — not just studied it — to map your options." },
              { n: "04", t: "Walk Away with a Roadmap", d: "Leave with a clear, practical plan for your education, skills, or next career move." },
            ].map((step, i) => (
              <FadeUp key={step.n} delay={i * 0.06}>
                <div className="border-t border-[#1A1C1A] pt-6 h-full">
                  <div className="font-serif text-5xl font-light text-[#C26D5C]">{step.n}</div>
                  <h3 className="font-serif text-2xl font-light mt-4 tracking-tight">{step.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#1A1C1A]/80">{step.d}</p>
                </div>
              </FadeUp>
            ))}
          </div>

          <FadeUp delay={0.3}>
            <div className="mt-20 flex flex-wrap items-center gap-4">
              <Link to="/book" data-testid="services-final-cta" className="btn-cf">
                Book a Free Career Call <ArrowUpRight size={14} />
              </Link>
              <p className="text-sm text-[#1A1C1A]/70 italic max-w-md">
                Available online nationwide, with onsite drives for schools, colleges &amp; institutions.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
